/**
 * The fluid field behind the transcript.
 *
 * An incompressible Navier-Stokes solver running entirely in fragment shaders,
 * used as a mouse toy: moving across the transcript stirs dye through a real
 * velocity field, so vortices shed, drift and interact rather than merely
 * smearing. Eight passes per frame at a 256-cell simulation grid, which is
 * resolution-independent of the window - the dye texture is what carries the
 * visible detail, and it is upscaled.
 *
 * Two things here are unusual and both exist to protect the text on top.
 *
 * The dye is clamped as it is deposited (DYE_CEILING). Dye is otherwise
 * unbounded: a slow scribble in one spot stacks it fifty times over, and then
 * no display setting can keep the field below the text's luminance. Clamping at
 * the splat makes the worst case a property of this file rather than of how
 * long somebody scrubbed the mouse.
 *
 * And the light theme composites the opposite way round. Emissive dye is
 * invisible on white and ruinous for near-black text, so under a light palette
 * the dye absorbs instead - Beer-Lambert, where a red-tinted parcel takes out
 * green and blue and the paper shows through everywhere else. It reads as ink
 * spreading on wet paper, and because absorption against white is bounded it
 * costs less contrast than the emissive path does on dark.
 *
 * It has no life of its own. Nothing moves unless the pointer moves it, the dye
 * decays to true black rather than towards it, and once the last of it is gone
 * the loop stops until somebody touches the transcript again - so an untouched
 * window costs nothing at all.
 *
 * The module is inert where it should be: no WebGL2, no mount; reduced motion,
 * no mount; hidden tab, no frames. It is also switchable, under Appearance -
 * see the preference at the bottom of this file - because it does cost some
 * text contrast and that should not be a decision made for somebody.
 */

/** Everything tunable, in one place. */
export const CONFIG = {
  SIM_RESOLUTION: 256,
  DYE_RESOLUTION: 1024,
  DENSITY_DISSIPATION: 0.36,
  VELOCITY_DISSIPATION: 0.28,
  PRESSURE: 0.99,
  PRESSURE_ITERATIONS: 20,
  CURL: 32,
  /*
   * Splat geometry, taken from how fast the pointer is actually going.
   *
   * A single radius cannot serve both ends of that. The fast figure below is the
   * constant this used to have, which is tuned for a quick sweep; used for a slow
   * deliberate drag as well it lays down the same broad blob, so a careful line
   * comes out as wide and formless as a flick. Slow now draws tight and fast
   * still spreads, with SPLAT_SPEED_REF the speed at which the fast end is
   * reached - in canvas widths per second, so it means the same thing on any
   * size of window.
   */
  SPLAT_RADIUS_SLOW: 0.04,
  SPLAT_RADIUS_FAST: 0.16,
  SPLAT_SPEED_REF:2.2,
  SPLAT_FORCE: 6000,
  /**
   * How quickly measured velocity follows the pointer, per event. Pointer events
   * arrive anywhere from 60 to 1000 times a second and the fastest of them are
   * jittery, so the speed that picks a radius is smoothed; the momentum handed to
   * the fluid is not, since that comes from distance actually travelled.
   */
  SPEED_SMOOTHING: 1.4,
  /**
   * Rotation injected per splat, so a flick seeds a vortex, not just shear.
   * In units of turn x canvas-widths-per-second, since that is what drives it
   * now - the number differs from the old one because the units do, not because
   * the feel was retuned.
   */
  SPIN_FORCE: 25,
  BLOOM_ITERATIONS: 5,
  BLOOM_RESOLUTION: 256,
  BLOOM_INTENSITY: 0.1,
  BLOOM_THRESHOLD: 0.32,
  BLOOM_SOFT_KNEE: 0.7,
  BLOOM_SATURATION: 1.45,
  /** Undoes the greying that additive overlap of differing hues produces. */
  DYE_SATURATION: 1.32,
  HUE_DRIFT: 0.005,
  /** The contrast lever. See the note at the top of this file. */
  DYE_CEILING: 0.9,
  EXPOSURE: 0.3,
  HIGHLIGHT_KNEE: 0.995,
  /** Light theme only: absorption per unit of dye. */
  INK_STRENGTH: 1.1,
  /**
   * Dye removed per second on top of the proportional dissipation above.
   * Without it decay is exponential and so never actually arrives: the last
   * percent of a stroke lingers as a visible smudge for a quarter of a minute.
   * This subtracts a fixed amount as well, which brings the tail to zero in
   * finite time and lets the transcript go properly black.
   */
  DENSITY_FADE: 0.30,
  DPR_CAP: 1.5,
};

const VERT = `#version 300 es
layout(location = 0) in vec2 aPosition;
out vec2 vUv, vL, vR, vT, vB;
uniform vec2 texelSize;
void main () {
  vUv = aPosition * 0.5 + 0.5;
  vL = vUv - vec2(texelSize.x, 0.0);  vR = vUv + vec2(texelSize.x, 0.0);
  vT = vUv + vec2(0.0, texelSize.y);  vB = vUv - vec2(0.0, texelSize.y);
  gl_Position = vec4(aPosition, 0.0, 1.0);
}`;

const HEAD = `#version 300 es
precision highp float; precision highp sampler2D;
in vec2 vUv, vL, vR, vT, vB;
out vec4 fragColor;
`;

/** Vorticity, as the curl of the velocity field. */
const CURL = `${HEAD}
uniform sampler2D uVelocity;
void main () {
  float L = texture(uVelocity, vL).y, R = texture(uVelocity, vR).y;
  float T = texture(uVelocity, vT).x, B = texture(uVelocity, vB).x;
  fragColor = vec4(0.5 * (R - L - T + B), 0.0, 0.0, 1.0);
}`;

/**
 * Vorticity confinement. Semi-Lagrangian advection is heavily dissipative and
 * eats exactly the small vortices that make a fluid look like one, so this puts
 * energy back at the vortex cores. It is not in Navier-Stokes; it is a
 * deliberate error cancelling a different error, and it is the single biggest
 * visual lever in the solver.
 */
const VORTICITY = `${HEAD}
uniform sampler2D uVelocity, uCurl;
uniform float curl, dt;
void main () {
  float L = texture(uCurl, vL).x, R = texture(uCurl, vR).x;
  float T = texture(uCurl, vT).x, B = texture(uCurl, vB).x;
  float C = texture(uCurl, vUv).x;
  vec2 force = 0.5 * vec2(abs(T) - abs(B), abs(R) - abs(L));
  force /= length(force) + 1e-4;
  force *= curl * C;
  force.y *= -1.0;
  vec2 vel = texture(uVelocity, vUv).xy + force * dt;
  fragColor = vec4(clamp(vel, -1000.0, 1000.0), 0.0, 1.0);
}`;

/** Divergence of the advected field, with reflective walls at the edges. */
const DIVERGENCE = `${HEAD}
uniform sampler2D uVelocity;
void main () {
  float L = texture(uVelocity, vL).x, R = texture(uVelocity, vR).x;
  float T = texture(uVelocity, vT).y, B = texture(uVelocity, vB).y;
  vec2 C = texture(uVelocity, vUv).xy;
  if (vL.x < 0.0) L = -C.x;
  if (vR.x > 1.0) R = -C.x;
  if (vT.y > 1.0) T = -C.y;
  if (vB.y < 0.0) B = -C.y;
  fragColor = vec4(0.5 * (R - L + T - B), 0.0, 0.0, 1.0);
}`;

/**
 * One Jacobi sweep of the pressure Poisson equation. Twenty of these is nowhere
 * near converged - there is real residual divergence left in the field - but
 * the eye reads local swirl, not global mass conservation.
 */
const PRESSURE = `${HEAD}
uniform sampler2D uPressure, uDivergence;
void main () {
  float L = texture(uPressure, vL).x, R = texture(uPressure, vR).x;
  float T = texture(uPressure, vT).x, B = texture(uPressure, vB).x;
  float div = texture(uDivergence, vUv).x;
  fragColor = vec4((L + R + B + T - div) * 0.25, 0.0, 0.0, 1.0);
}`;

/** Subtracting the pressure gradient leaves a divergence-free field. */
const GRADIENT = `${HEAD}
uniform sampler2D uPressure, uVelocity;
void main () {
  float L = texture(uPressure, vL).x, R = texture(uPressure, vR).x;
  float T = texture(uPressure, vT).x, B = texture(uPressure, vB).x;
  fragColor = vec4(texture(uVelocity, vUv).xy - vec2(R - L, T - B), 0.0, 1.0);
}`;

/**
 * Semi-Lagrangian advection: trace backwards and interpolate. Unconditionally
 * stable, because bilinear interpolation is a convex combination and so can
 * never amplify. Hardware filtering does the interpolation.
 */
const ADVECTION = `${HEAD}
uniform sampler2D uVelocity, uSource;
uniform vec2 uSimTexel;
uniform float dt, dissipation, fade;
void main () {
  vec2 coord = vUv - dt * texture(uVelocity, vUv).xy * uSimTexel;
  vec4 result = texture(uSource, coord) / (1.0 + dissipation * dt);
  // Shrink towards zero by a fixed amount, rather than clamping at it: this
  // pass also carries velocity, which is signed, and a plain max() would turn
  // every leftward flow into a standstill. With fade at 0 it is a no-op.
  float f = fade * dt;
  fragColor = result - clamp(result, -f, f);
}`;

const CLEAR = `${HEAD}
uniform sampler2D uTexture;
uniform float value;
void main () { fragColor = value * texture(uTexture, vUv); }`;

/**
 * Velocity splat. Alongside the linear momentum it adds a tangential term,
 * which has non-zero curl, so a flick seeds an actual vortex instead of a shear
 * layer that may or may not roll up on its own.
 */
const SPLAT_VEL = `${HEAD}
uniform sampler2D uTarget;
uniform float aspectRatio, radius, spin;
uniform vec2 point, vel;
void main () {
  vec2 p = vUv - point;
  p.x *= aspectRatio;
  float g = exp(-dot(p, p) / radius);
  vec2 add = (vel + vec2(-p.y, p.x) * spin) * g;
  fragColor = vec4(texture(uTarget, vUv).xy + add, 0.0, 1.0);
}`;

/** Dye splat, clamped. The clamp is what bounds the contrast cost. */
const SPLAT_DYE = `${HEAD}
uniform sampler2D uTarget;
uniform float aspectRatio, radius, ceiling;
uniform vec2 point;
uniform vec3 color;
void main () {
  vec2 p = vUv - point;
  p.x *= aspectRatio;
  vec3 c = texture(uTarget, vUv).rgb + exp(-dot(p, p) / radius) * color;
  fragColor = vec4(min(c, vec3(ceiling)), 1.0);
}`;

const BLOOM_PREFILTER = `${HEAD}
uniform sampler2D uTexture;
uniform vec3 curve;
uniform float threshold;
void main () {
  vec3 c = texture(uTexture, vUv).rgb;
  float br = max(c.r, max(c.g, c.b));
  float rq = clamp(br - curve.x, 0.0, curve.y);
  rq = curve.z * rq * rq;
  c *= max(rq, br - threshold) / max(br, 1e-4);
  fragColor = vec4(c, 1.0);
}`;

const BLOOM_BLUR = `${HEAD}
uniform sampler2D uTexture;
void main () {
  fragColor = (texture(uTexture, vL) + texture(uTexture, vR)
             + texture(uTexture, vT) + texture(uTexture, vB)) * 0.25;
}`;

const BLOOM_FINAL = `${HEAD}
uniform sampler2D uTexture;
uniform float intensity;
void main () {
  fragColor = (texture(uTexture, vL) + texture(uTexture, vR)
             + texture(uTexture, vT) + texture(uTexture, vB)) * 0.25 * intensity;
}`;

/**
 * Composite. `pageBg` is the palette's own --bg, so an unstirred field renders
 * the transcript exactly the colour the rest of the application is; the dye is
 * a departure from it rather than a replacement for it.
 */
const DISPLAY = `${HEAD}
uniform sampler2D uTexture, uBloom;
uniform float bloomSat, dyeSat, knee, exposure, ink;
uniform vec3 pageBg;
uniform int mode;
const vec3 LUMA = vec3(0.2126, 0.7152, 0.0722);
float hash12 (vec2 p) {
  vec3 p3 = fract(p.xyx * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
void main () {
  vec3 c = texture(uTexture, vUv).rgb;
  c = max(mix(vec3(dot(c, LUMA)), c, dyeSat), 0.0);
  float dither = (hash12(gl_FragCoord.xy) - 0.5) / 255.0;

  if (mode == 1) {
    float density = max(c.r, max(c.g, c.b));
    vec3 tint = density > 1e-4 ? c / density : vec3(1.0);
    vec3 absorb = (1.0 - tint) * density * exposure * ink;
    fragColor = vec4(pageBg * exp(-absorb) + dither, 1.0);
    return;
  }

  vec3 b = texture(uBloom, vUv).rgb;
  b = mix(vec3(dot(b, LUMA)), b, bloomSat);
  c += b;
  c *= exposure;

  // Hue-preserving roll-off: one common scale factor off the max channel, so
  // the channels keep their ratios and a bright colour intensifies instead of
  // sliding to white. Per-channel clamping does the opposite.
  float m = max(c.r, max(c.g, c.b));
  if (m > knee) {
    float t = knee + (1.0 - knee) * (1.0 - exp(-(m - knee) / (1.0 - knee)));
    c *= t / m;
  }
  fragColor = vec4(pageBg + c + dither, 1.0);
}`;

/** Parses `rgb(r, g, b)` or `#rrggbb` into three 0..1 channels. */
function parseColour(value) {
  const text = String(value || '').trim();
  const fn = text.match(/^rgba?\(([^)]+)\)$/i);
  if (fn) {
    const parts = fn[1].split(/[\s,/]+/).filter(Boolean).map(Number);
    if (parts.length >= 3) return parts.slice(0, 3).map((n) => n / 255);
  }
  const hex = text.match(/^#?([0-9a-f]{6})$/i);
  if (hex) {
    const n = parseInt(hex[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => v / 255);
  }
  return [0, 0, 0];
}

/** Relative luminance, good enough to tell a light palette from a dark one. */
function roughLuminance([r, g, b]) {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/* ------------------------------------------------------- pointer kinematics */

/*
 * Deliberately outside mountFluid, and taking the aspect ratio and the config as
 * arguments rather than closing over a canvas.
 *
 * None of this needs a GL context: it is the arithmetic that turns a stream of
 * pointer events into a speed, a heading and a radius, which is the part with
 * decisions in it and therefore the part worth testing. See scripts/fluid-test.mjs.
 */

function makePointer(x, y, at) {
  return {
    x,
    y,
    px: x,
    py: y,
    /*
     * Movement banked since the last splat, and the velocity it happened at.
     *
     * The two are kept apart because they answer different questions. `ax`/`ay`
     * accumulate, so nothing a pointer does between two frames is lost - a mouse
     * polling at 1000Hz fires some sixteen events per frame, and overwriting a
     * single delta each time threw fifteen of them away and pushed the fluid
     * with whatever fragment happened to land last. Summing instead makes the
     * push proportional to the distance actually travelled, which is the same
     * on any mouse. `vx`/`vy` are smoothed and only choose the radius.
     */
    ax: 0,
    ay: 0,
    vx: 0,
    vy: 0,
    speed: 0,
    spin: 0,
    at,
    moved: false,
  };
}

function movePointer(p, x, y, at, aspect, config = CONFIG) {
  // Distance in canvas widths, with the short axis corrected, so a diagonal is
  // as long as it looks and speed does not depend on the window's shape.
  const stepX = (x - p.x) * (aspect < 1 ? aspect : 1);
  const stepY = (y - p.y) * (aspect > 1 ? 1 / aspect : 1);
  p.px = p.x;
  p.py = p.y;
  p.x = x;
  p.y = y;
  p.ax += stepX;
  p.ay += stepY;

  /*
   * Seconds since the previous event, from the event's own timestamp rather
   * than a clock read here - the two differ by however long the main thread was
   * busy, which is exactly when this would be worst. Bounded at both ends: a
   * coalesced pair can report no elapsed time at all, and a pointer returning
   * after a pause must not read as one enormous velocity.
   */
  const elapsed = Math.min(Math.max((at - p.at) / 1000, 1 / 1000), 1 / 15);
  p.at = at;

  const instantX = stepX / elapsed;
  const instantY = stepY / elapsed;
  // Held before the smoothing overwrites them, because the turn below is
  // between where the pointer was going and where it is going now.
  const wasX = p.vx;
  const wasY = p.vy;
  const k = config.SPEED_SMOOTHING;
  p.vx += (instantX - p.vx) * k;
  p.vy += (instantY - p.vy) * k;
  p.speed = Math.hypot(p.vx, p.vy);

  // Signed turn rate, from the cross product of the two: moving in a straight
  // line injects no curl, curving or flicking injects a lot.
  const cross = wasX * instantY - wasY * instantX;
  const mags = Math.hypot(wasX, wasY) * Math.hypot(instantX, instantY);
  const turn = mags > 1e-9 ? Math.max(-1, Math.min(1, cross / mags)) : 0;
  p.spin = Math.max(-400, Math.min(400, turn * p.speed * config.SPIN_FORCE));
  p.moved = stepX !== 0 || stepY !== 0;
}

/**
 * The radius a splat gets at this pointer's speed: tight when it is barely
 * moving, the old fixed figure once it reaches SPLAT_SPEED_REF, and no wider
 * beyond that. Smoothstepped rather than linear so neither end has a corner in
 * it that the hand can feel.
 */
function spreadFor(speed, config = CONFIG) {
  const t = Math.min(1, speed / config.SPLAT_SPEED_REF);
  const eased = t * t * (3 - 2 * t);
  return config.SPLAT_RADIUS_SLOW + (config.SPLAT_RADIUS_FAST - config.SPLAT_RADIUS_SLOW) * eased;
}

/**
 * Starts the solver on `canvas`, stirred by pointer movement over `surface`.
 *
 * Returns a controller with `destroy()`. Returns null - having done nothing at
 * all - when the environment cannot or should not run it.
 */
export function mountFluid(canvas, surface, options = {}) {
  if (!canvas || !surface) return null;

  const reduceMotion =
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion) return null;

  const gl = canvas.getContext('webgl2', {
    alpha: false,
    depth: false,
    stencil: false,
    antialias: false,
    preserveDrawingBuffer: false,
  });
  if (!gl || !gl.getExtension('EXT_color_buffer_float')) return null;

  const config = { ...CONFIG, ...options };

  // ---------------------------------------------------------------- programs

  function compile(type, source) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      throw new Error(gl.getShaderInfoLog(shader) || 'shader compile failed');
    }
    return shader;
  }

  function makeProgram(fragment) {
    const program = gl.createProgram();
    gl.attachShader(program, compile(gl.VERTEX_SHADER, VERT));
    gl.attachShader(program, compile(gl.FRAGMENT_SHADER, fragment));
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      throw new Error(gl.getProgramInfoLog(program) || 'link failed');
    }
    const u = {};
    const count = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
    for (let i = 0; i < count; i += 1) {
      const name = gl.getActiveUniform(program, i).name;
      u[name] = gl.getUniformLocation(program, name);
    }
    return { program, u, bind: () => gl.useProgram(program) };
  }

  let prog;
  try {
    prog = {
      curl: makeProgram(CURL),
      vorticity: makeProgram(VORTICITY),
      divergence: makeProgram(DIVERGENCE),
      pressure: makeProgram(PRESSURE),
      gradient: makeProgram(GRADIENT),
      advection: makeProgram(ADVECTION),
      clear: makeProgram(CLEAR),
      splatVel: makeProgram(SPLAT_VEL),
      splatDye: makeProgram(SPLAT_DYE),
      prefilter: makeProgram(BLOOM_PREFILTER),
      blur: makeProgram(BLOOM_BLUR),
      bloomFinal: makeProgram(BLOOM_FINAL),
      display: makeProgram(DISPLAY),
    };
  } catch {
    // A driver that will not compile this is a driver we quietly do without.
    return null;
  }

  // A full-screen triangle: one fewer vertex than a quad, and no diagonal seam.
  gl.bindVertexArray(gl.createVertexArray());
  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

  function blit(target) {
    if (target === null) {
      gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    } else {
      gl.viewport(0, 0, target.width, target.height);
      gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo);
    }
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  // ------------------------------------------------------------ framebuffers

  const created = [];

  function createFBO(w, h, internal, format, type, filter) {
    gl.activeTexture(gl.TEXTURE0);
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, internal, w, h, 0, format, type, null);

    const fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    gl.viewport(0, 0, w, h);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);

    const target = {
      texture,
      fbo,
      width: w,
      height: h,
      texelSizeX: 1 / w,
      texelSizeY: 1 / h,
      attach(id) {
        gl.activeTexture(gl.TEXTURE0 + id);
        gl.bindTexture(gl.TEXTURE_2D, texture);
        return id;
      },
    };
    created.push(target);
    return target;
  }

  function createDoubleFBO(...args) {
    return {
      read: createFBO(...args),
      write: createFBO(...args),
      width: args[0],
      height: args[1],
      texelSizeX: 1 / args[0],
      texelSizeY: 1 / args[1],
      swap() {
        const t = this.read;
        this.read = this.write;
        this.write = t;
      },
    };
  }

  function releaseTargets() {
    for (const t of created) {
      gl.deleteFramebuffer(t.fbo);
      gl.deleteTexture(t.texture);
    }
    created.length = 0;
  }

  /** Frees one target, rather than everything. */
  function dispose(target) {
    if (!target) return;
    const at = created.indexOf(target);
    if (at >= 0) created.splice(at, 1);
    gl.deleteFramebuffer(target.fbo);
    gl.deleteTexture(target.texture);
  }

  /**
   * Moves a field to a new size, bringing what is in it along.
   *
   * The CLEAR program multiplies a texture by a constant, so at 1.0 it is a
   * copy - which is all this needs, and one fewer shader than writing a
   * dedicated one. Sampling is over the full 0..1 range, so a field whose aspect
   * ratio changed arrives stretched; that is a much smaller lie than the
   * alternative, which is that it arrives empty.
   */
  function resizeFBO(target, w, h, internal, format, type, filter) {
    const next = createFBO(w, h, internal, format, type, filter);
    prog.clear.bind();
    gl.uniform2f(prog.clear.u.texelSize, target.texelSizeX, target.texelSizeY);
    gl.uniform1i(prog.clear.u.uTexture, target.attach(0));
    gl.uniform1f(prog.clear.u.value, 1);
    blit(next);
    dispose(target);
    return next;
  }

  /**
   * The same for a double-buffered field. Only `read` holds anything worth
   * keeping - `write` is overwritten before it is ever sampled - so it is the
   * one that is copied and the other is simply replaced.
   */
  function resizeDoubleFBO(target, w, h, internal, format, type, filter) {
    if (target.width === w && target.height === h) return target;
    target.read = resizeFBO(target.read, w, h, internal, format, type, filter);
    dispose(target.write);
    target.write = createFBO(w, h, internal, format, type, filter);
    target.width = w;
    target.height = h;
    target.texelSizeX = 1 / w;
    target.texelSizeY = 1 / h;
    return target;
  }

  const HALF = gl.HALF_FLOAT;
  let dye;
  let velocity;
  let pressure;
  let divergence;
  let curlTarget;
  let bloom;
  let bloomChain = [];

  function resolutionFor(base) {
    let aspect = gl.drawingBufferWidth / gl.drawingBufferHeight;
    if (!Number.isFinite(aspect) || aspect === 0) aspect = 1;
    if (aspect < 1) aspect = 1 / aspect;
    const min = Math.round(base);
    const max = Math.round(base * aspect);
    return gl.drawingBufferWidth > gl.drawingBufferHeight
      ? { width: max, height: min }
      : { width: min, height: max };
  }

  /**
   * Allocates the fields, or moves the existing ones to a new size.
   *
   * This runs on every resize, and a resize is not rare: the canvas changes
   * shape whenever the layout does - somebody turning a camera on, the jukebox
   * opening, the drag between the stage and the transcript, a phone being
   * turned. It used to free everything and allocate afresh, which is correct but
   * starts the simulation from nothing, so the picture went black and began
   * again every time any of that happened.
   *
   * Dye and velocity are therefore carried over. They are the state: what the
   * fluid looks like and where it is going. The rest are working space -
   * divergence and curl are written from scratch at the top of every step, and
   * pressure is an iterative guess that re-converges within a frame or two - so
   * those are replaced empty, which costs nothing visible and keeps this simple.
   */
  function initFramebuffers() {
    const sim = resolutionFor(config.SIM_RESOLUTION);
    const d = resolutionFor(config.DYE_RESOLUTION);

    dye = dye
      ? resizeDoubleFBO(dye, d.width, d.height, gl.RGBA16F, gl.RGBA, HALF, gl.LINEAR)
      : createDoubleFBO(d.width, d.height, gl.RGBA16F, gl.RGBA, HALF, gl.LINEAR);
    velocity = velocity
      ? resizeDoubleFBO(velocity, sim.width, sim.height, gl.RG16F, gl.RG, HALF, gl.LINEAR)
      : createDoubleFBO(sim.width, sim.height, gl.RG16F, gl.RG, HALF, gl.LINEAR);

    // Working space: freed and made again at the new size.
    if (pressure) {
      dispose(pressure.read);
      dispose(pressure.write);
    }
    dispose(divergence);
    dispose(curlTarget);
    pressure = createDoubleFBO(sim.width, sim.height, gl.R16F, gl.RED, HALF, gl.NEAREST);
    divergence = createFBO(sim.width, sim.height, gl.R16F, gl.RED, HALF, gl.NEAREST);
    curlTarget = createFBO(sim.width, sim.height, gl.R16F, gl.RED, HALF, gl.NEAREST);

    const b = resolutionFor(config.BLOOM_RESOLUTION);
    dispose(bloom);
    for (const target of bloomChain) dispose(target);
    bloom = createFBO(b.width, b.height, gl.RGBA16F, gl.RGBA, HALF, gl.LINEAR);
    bloomChain = [];
    for (let i = 0; i < config.BLOOM_ITERATIONS; i += 1) {
      const w = b.width >> (i + 1);
      const h = b.height >> (i + 1);
      if (w < 2 || h < 2) break;
      bloomChain.push(createFBO(w, h, gl.RGBA16F, gl.RGBA, HALF, gl.LINEAR));
    }
  }

  // ------------------------------------------------------------------ colour

  let hueAngle = Math.random() * Math.PI * 2;

  /**
   * Hue drifts slowly along a stroke. Rotating it quickly looks vivid frame by
   * frame, but dye is additive, so neighbouring splats of differing hue average
   * out and the field goes grey within a second. DYE_SATURATION in the display
   * pass is what buys back enough headroom for the drift to be this fast.
   */
  function nextHue(jump) {
    hueAngle += jump;
    const s = 0.62;
    return [
      (0.5 + 0.5 * Math.cos(hueAngle)) * s,
      (0.5 + 0.5 * Math.cos(hueAngle + 2.094)) * s,
      (0.5 + 0.5 * Math.cos(hueAngle + 4.188)) * s,
    ];
  }

  // ------------------------------------------------------------------ splats

  function splat(x, y, dx, dy, spin, colour, spread) {
    const aspect = canvas.width / canvas.height;
    let radius = spread / 100;
    if (aspect > 1) radius *= aspect;

    prog.splatVel.bind();
    gl.uniform2f(prog.splatVel.u.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(prog.splatVel.u.uTarget, velocity.read.attach(0));
    gl.uniform1f(prog.splatVel.u.aspectRatio, aspect);
    gl.uniform2f(prog.splatVel.u.point, x, y);
    gl.uniform2f(prog.splatVel.u.vel, dx, dy);
    gl.uniform1f(prog.splatVel.u.spin, spin);
    gl.uniform1f(prog.splatVel.u.radius, radius);
    blit(velocity.write);
    velocity.swap();

    prog.splatDye.bind();
    gl.uniform2f(prog.splatDye.u.texelSize, dye.texelSizeX, dye.texelSizeY);
    gl.uniform1i(prog.splatDye.u.uTarget, dye.read.attach(0));
    gl.uniform1f(prog.splatDye.u.aspectRatio, aspect);
    gl.uniform2f(prog.splatDye.u.point, x, y);
    gl.uniform3f(prog.splatDye.u.color, colour[0], colour[1], colour[2]);
    gl.uniform1f(prog.splatDye.u.radius, radius);
    gl.uniform1f(prog.splatDye.u.ceiling, config.DYE_CEILING);
    blit(dye.write);
    dye.swap();
  }

  // ------------------------------------------------------------------- input

  const pointers = new Map();

  // Listening on the scrolling surface rather than the canvas, because the
  // canvas is underneath the text. Passive, so selection and scrolling are
  // untouched.
  function onPointerMove(event) {
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = (event.clientX - rect.left) / rect.width;
    const y = 1 - (event.clientY - rect.top) / rect.height;
    // The event's own timestamp, so a busy main thread cannot inflate the
    // measured speed by charging it for time spent elsewhere.
    const at = typeof event.timeStamp === 'number' ? event.timeStamp : performance.now();
    if (!pointers.has(event.pointerId)) pointers.set(event.pointerId, makePointer(x, y, at));
    movePointer(pointers.get(event.pointerId), x, y, at, canvas.width / canvas.height, config);
    wake();
  }

  function onPointerGone(event) {
    pointers.delete(event.pointerId);
  }

  surface.addEventListener('pointermove', onPointerMove, { passive: true });
  surface.addEventListener('pointerleave', onPointerGone, { passive: true });
  surface.addEventListener('pointercancel', onPointerGone, { passive: true });

  // -------------------------------------------------------------------- idle

  /**
   * How much simulated time after the last splat the field is certainly black,
   * from the decay the config actually asks for: with a fixed amount removed
   * per second as well as a proportional one, a full-strength parcel reaches
   * exactly zero rather than approaching it. Derived rather than guessed so
   * that retuning either dissipation cannot quietly leave the loop stopping
   * over a frame that still has dye in it.
   */
  function secondsUntilBlack() {
    const d = config.DENSITY_DISSIPATION;
    const f = config.DENSITY_FADE;
    if (f <= 0) return Infinity;
    if (d <= 0) return config.DYE_CEILING / f;
    return (1 / d) * Math.log(1 + (d * config.DYE_CEILING) / f);
  }

  const idleSeconds = secondsUntilBlack() + 0.5;

  /*
   * Simulated seconds since the last splat - deliberately not wall-clock ones.
   * The step below caps dt at a sixtieth of a second, so on a slow machine or a
   * throttled tab the dye decays slower than the clock runs. Timing the stop by
   * the wall would freeze the loop part-way through the fade and leave the last
   * smear of dye sitting on the canvas indefinitely.
   */
  let quiet = Infinity;

  // -------------------------------------------------------------------- step

  function step(dt) {
    gl.disable(gl.BLEND);

    prog.curl.bind();
    gl.uniform2f(prog.curl.u.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(prog.curl.u.uVelocity, velocity.read.attach(0));
    blit(curlTarget);

    prog.vorticity.bind();
    gl.uniform2f(prog.vorticity.u.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(prog.vorticity.u.uVelocity, velocity.read.attach(0));
    gl.uniform1i(prog.vorticity.u.uCurl, curlTarget.attach(1));
    gl.uniform1f(prog.vorticity.u.curl, config.CURL);
    gl.uniform1f(prog.vorticity.u.dt, dt);
    blit(velocity.write);
    velocity.swap();

    prog.divergence.bind();
    gl.uniform2f(prog.divergence.u.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(prog.divergence.u.uVelocity, velocity.read.attach(0));
    blit(divergence);

    prog.clear.bind();
    gl.uniform2f(prog.clear.u.texelSize, pressure.texelSizeX, pressure.texelSizeY);
    gl.uniform1i(prog.clear.u.uTexture, pressure.read.attach(0));
    gl.uniform1f(prog.clear.u.value, config.PRESSURE);
    blit(pressure.write);
    pressure.swap();

    prog.pressure.bind();
    gl.uniform2f(prog.pressure.u.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(prog.pressure.u.uDivergence, divergence.attach(0));
    for (let i = 0; i < config.PRESSURE_ITERATIONS; i += 1) {
      gl.uniform1i(prog.pressure.u.uPressure, pressure.read.attach(1));
      blit(pressure.write);
      pressure.swap();
    }

    prog.gradient.bind();
    gl.uniform2f(prog.gradient.u.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(prog.gradient.u.uPressure, pressure.read.attach(0));
    gl.uniform1i(prog.gradient.u.uVelocity, velocity.read.attach(1));
    blit(velocity.write);
    velocity.swap();

    prog.advection.bind();
    gl.uniform2f(prog.advection.u.uSimTexel, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1f(prog.advection.u.dt, dt);

    gl.uniform2f(prog.advection.u.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    const velocityId = velocity.read.attach(0);
    gl.uniform1i(prog.advection.u.uVelocity, velocityId);
    gl.uniform1i(prog.advection.u.uSource, velocityId);
    gl.uniform1f(prog.advection.u.dissipation, config.VELOCITY_DISSIPATION);
    gl.uniform1f(prog.advection.u.fade, 0);
    blit(velocity.write);
    velocity.swap();

    gl.uniform2f(prog.advection.u.texelSize, dye.texelSizeX, dye.texelSizeY);
    gl.uniform1i(prog.advection.u.uVelocity, velocity.read.attach(0));
    gl.uniform1i(prog.advection.u.uSource, dye.read.attach(1));
    gl.uniform1f(prog.advection.u.dissipation, config.DENSITY_DISSIPATION);
    gl.uniform1f(prog.advection.u.fade, config.DENSITY_FADE);
    blit(dye.write);
    dye.swap();
  }

  function applyBloom(source, dest) {
    if (bloomChain.length < 2) return;
    let last = dest;

    gl.disable(gl.BLEND);
    prog.prefilter.bind();
    const knee = config.BLOOM_THRESHOLD * config.BLOOM_SOFT_KNEE + 1e-4;
    gl.uniform3f(
      prog.prefilter.u.curve,
      config.BLOOM_THRESHOLD - knee,
      knee * 2,
      0.25 / knee,
    );
    gl.uniform1f(prog.prefilter.u.threshold, config.BLOOM_THRESHOLD);
    gl.uniform1i(prog.prefilter.u.uTexture, source.attach(0));
    blit(last);

    prog.blur.bind();
    for (const target of bloomChain) {
      gl.uniform2f(prog.blur.u.texelSize, last.texelSizeX, last.texelSizeY);
      gl.uniform1i(prog.blur.u.uTexture, last.attach(0));
      blit(target);
      last = target;
    }

    gl.blendFunc(gl.ONE, gl.ONE);
    gl.enable(gl.BLEND);
    for (let i = bloomChain.length - 2; i >= 0; i -= 1) {
      const target = bloomChain[i];
      gl.uniform2f(prog.blur.u.texelSize, last.texelSizeX, last.texelSizeY);
      gl.uniform1i(prog.blur.u.uTexture, last.attach(0));
      blit(target);
      last = target;
    }
    gl.disable(gl.BLEND);

    prog.bloomFinal.bind();
    gl.uniform2f(prog.bloomFinal.u.texelSize, last.texelSizeX, last.texelSizeY);
    gl.uniform1i(prog.bloomFinal.u.uTexture, last.attach(0));
    gl.uniform1f(prog.bloomFinal.u.intensity, config.BLOOM_INTENSITY);
    blit(dest);
  }

  // ------------------------------------------------------------------ palette

  let pageBg = [0, 0, 0];
  let light = false;

  /**
   * Reads --bg back out of the stylesheet, exactly as theme.js does for the
   * browser chrome: the palette has one home, and this follows it rather than
   * duplicating it. Which way the dye composites is decided by how light that
   * colour turns out to be, so all three theme settings are covered without
   * this module knowing which one is in force.
   */
  function readPalette() {
    const root = document.documentElement;
    const value = getComputedStyle(root).getPropertyValue('--bg');
    pageBg = parseColour(value);
    light = roughLuminance(pageBg) > 0.5;
  }

  /**
   * Re-reads the palette and paints one frame with it.
   *
   * The frame is the point. The canvas covers the transcript, so whatever it
   * last drew is the transcript's background until something draws again - and
   * the loop stops itself once the dye is gone. Updating the uniforms alone
   * would leave a switch to light theme showing the old dark frame under the
   * new dark text, which is unreadable.
   */
  function repaint() {
    readPalette();
    // Observers are attached before the first initFramebuffers(), so a
    // mutation arriving early would otherwise render against nothing.
    if (dye) render();
  }

  function render() {
    if (!light) applyBloom(dye.read, bloom);
    prog.display.bind();
    gl.uniform2f(prog.display.u.texelSize, dye.texelSizeX, dye.texelSizeY);
    gl.uniform1i(prog.display.u.uTexture, dye.read.attach(0));
    gl.uniform1i(prog.display.u.uBloom, bloom.attach(1));
    gl.uniform1f(prog.display.u.bloomSat, config.BLOOM_SATURATION);
    gl.uniform1f(prog.display.u.dyeSat, config.DYE_SATURATION);
    gl.uniform1f(prog.display.u.exposure, config.EXPOSURE);
    gl.uniform1f(prog.display.u.knee, config.HIGHLIGHT_KNEE);
    gl.uniform1f(prog.display.u.ink, config.INK_STRENGTH);
    gl.uniform1i(prog.display.u.mode, light ? 1 : 0);
    gl.uniform3f(prog.display.u.pageBg, pageBg[0], pageBg[1], pageBg[2]);
    blit(null);
  }

  // -------------------------------------------------------------------- loop

  function resize() {
    const dpr = Math.min(globalThis.devicePixelRatio || 1, config.DPR_CAP);
    const w = Math.max(1, Math.round(canvas.clientWidth * dpr));
    const h = Math.max(1, Math.round(canvas.clientHeight * dpr));
    if (canvas.width === w && canvas.height === h) return false;
    canvas.width = w;
    canvas.height = h;
    return true;
  }

  let raf = 0;
  let last = performance.now();
  let destroyed = false;

  function frame(now) {
    if (destroyed) return;
    raf = requestAnimationFrame(frame);
    if (resize()) initFramebuffers();

    const dt = Math.min((now - last) / 1000, 1 / 60);
    last = now;

    quiet += dt;
    for (const p of pointers.values()) {
      if (!p.moved) continue;
      p.moved = false;
      quiet = 0;
      /*
       * The banked movement is what pushes, and it is spent here: everything the
       * pointer did since the last frame goes in at once and the account resets,
       * so a stroke hands over the same momentum whatever rate the mouse reports
       * at. The radius comes from the smoothed speed instead, which is the part
       * that should not flicker between one frame and the next.
       */
      splat(
        p.x,
        p.y,
        p.ax * config.SPLAT_FORCE,
        p.ay * config.SPLAT_FORCE,
        p.spin,
        nextHue(config.HUE_DRIFT),
        spreadFor(p.speed, config),
      );
      p.ax = 0;
      p.ay = 0;
    }

    step(dt);
    render();

    // Once the dye is gone there is nothing left to simulate, so stop rather
    // than grind out identical black frames behind a transcript nobody is
    // touching. The next pointer movement starts it again.
    if (quiet > idleSeconds) {
      cancelAnimationFrame(raf);
      raf = 0;
    }
  }

  /**
   * Restarts the loop if it stopped itself while idle.
   *
   * Deliberately not conditioned on document.hidden. Input arriving at all is
   * the better signal, and some embeddings report a page as hidden while still
   * servicing animation frames - refusing there leaves the solver unable to
   * revive until a visibility change that may never arrive. A tab that really
   * is in the background has its frames throttled by the browser regardless,
   * so waking costs nothing.
   */
  function wake() {
    if (destroyed || raf) return;
    last = performance.now();
    raf = requestAnimationFrame(frame);
  }

  function onVisibility() {
    if (document.hidden) {
      cancelAnimationFrame(raf);
      raf = 0;
    } else {
      wake();
    }
  }

  const themeObserver = new MutationObserver(repaint);
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme'],
  });

  const systemTheme =
    typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null;
  if (systemTheme) systemTheme.addEventListener('change', repaint);

  const resizeObserver = new ResizeObserver(() => {
    // Rendering here as well as reinitialising, because the loop may have
    // stopped itself: without a frame the canvas would keep showing its old
    // contents stretched to the new size.
    if (resize()) {
      initFramebuffers();
      render();
    }
  });
  resizeObserver.observe(canvas);

  document.addEventListener('visibilitychange', onVisibility);

  readPalette();
  resize();
  initFramebuffers();
  raf = requestAnimationFrame(frame);

  return {
    /** Re-reads the palette and repaints. The observers usually get there first. */
    refresh: repaint,
    destroy() {
      destroyed = true;
      cancelAnimationFrame(raf);
      themeObserver.disconnect();
      resizeObserver.disconnect();
      if (systemTheme) systemTheme.removeEventListener('change', repaint);
      document.removeEventListener('visibilitychange', onVisibility);
      surface.removeEventListener('pointermove', onPointerMove);
      surface.removeEventListener('pointerleave', onPointerGone);
      surface.removeEventListener('pointercancel', onPointerGone);
      releaseTargets();
    },
  };
}

/* -------------------------------------------------------------- preference */

/*
 * On/off, remembered, and the mounting that goes with it.
 *
 * Stored as the string 'off' rather than a boolean so that anything unreadable -
 * a cleared store, a value written by an older build - falls back to on, which
 * is what an absent preference should mean.
 */

const STORAGE_KEY = 'webchat.fluid';

function readPreference() {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}

let wanted = readPreference();
let active = null;
let mountedCanvas = null;
let mountedSurface = null;
let support = null;

/**
 * Whether this browser could run it at all. Costs a throwaway context, so the
 * answer is kept; it cannot change within a page's life. Reduced motion counts
 * as unsupported here, which is what keeps the switch from offering something
 * that would then refuse to happen.
 */
export function fluidSupported() {
  if (support !== null) return support;
  if (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches) {
    support = false;
    return support;
  }
  try {
    const probe = document.createElement('canvas').getContext('webgl2');
    support = Boolean(probe && probe.getExtension('EXT_color_buffer_float'));
  } catch {
    support = false;
  }
  return support;
}

/** Whether the effect is switched on. Says nothing about whether it can run. */
export function fluidEnabled() {
  return wanted;
}

function show(visible) {
  if (mountedCanvas) mountedCanvas.hidden = !visible;
}

/**
 * Remembers `value` and starts or stops the solver to match. Stopping releases
 * every texture and framebuffer rather than merely pausing, because the point of
 * turning it off is to stop paying for it.
 */
export function setFluidEnabled(value) {
  wanted = Boolean(value);
  try {
    localStorage.setItem(STORAGE_KEY, wanted ? 'on' : 'off');
  } catch {
    /* storage may be unavailable; the choice still applies to this page */
  }
  if (wanted && !active && mountedCanvas) {
    // Reveal before mounting: a hidden element measures 0x0, and the solver
    // sizes its textures off the canvas as it starts. Mounting first leaves it
    // at 1x1 until some later frame notices, which is a wait of unbounded
    // length in a background tab.
    show(true);
    active = mountFluid(mountedCanvas, mountedSurface);
    show(Boolean(active));
  } else if (!wanted && active) {
    active.destroy();
    active = null;
    // A destroyed solver leaves its last frame on the canvas, so take the
    // element out of the picture rather than leaving a frozen smear behind.
    show(false);
  }
  return wanted;
}

/** Wires the effect to its canvas at startup, honouring the saved preference. */
export function setupFluid(canvas, surface) {
  mountedCanvas = canvas;
  mountedSurface = surface;
  if (!canvas || !surface) return null;
  if (wanted) active = mountFluid(canvas, surface);
  show(Boolean(active));
  return active;
}

export { makePointer, movePointer, spreadFor };

export default {
  mountFluid,
  setupFluid,
  fluidEnabled,
  setFluidEnabled,
  fluidSupported,
  makePointer,
  movePointer,
  spreadFor,
  CONFIG,
};
